import { PDFDocument } from "pdf-lib";
import { getFullName, yearsSinceDate } from "~/shared/utils";

export async function printEligibilityPdf({ parent, household }, child, pdfOverride) {
    const pdf = await (pdfOverride ? PDFDocument.load(pdfOverride) : readPdf("/EligibilityForm.pdf"));
    const form = pdf.getForm();

    const { firstName, lastName } = child;
    const { familyUnitSize, grossMonthlyIncome } = household;
    const [YYDOB, MMDob, DDDob] = child.dateOfBirth.split('-');

    // Sections 1, 2 & 5
    let data = {
        firstName, lastName,
        MI: child.middleName?.[0],
        MMDob, DDDob, YYDOB,
        ParentFullName: `${parent.lastName}, ${parent.firstName}${parent.middleName?.[0] ? `, ${parent.middleName?.[0]}` : ""}`,
        ParentPrintedName: getFullName(parent),
        CellPhone: parent.mainPhone,
        SSN: child.socialSecurity.split("-"),
        ParentAddress: parent.streetAddress,
        ParentCity: parent.city,
        ParentState: parent.state,
        ZipCode: parent.zipcode,
        Date: new Date().toLocaleDateString("en-US"),
        [`Gender${child.gender}`]: "X",
        ...ynField("Foster", child.inFosterCare),
        ...ynField("Citizen", child.isUSCitizenOrQualifiedAlien),
        ...ynField("GARes", child.isGAResident),
        ...fillFieldIf("YAOver18", child.is18AndEnrolledInSchool),
        ...fillFieldIf("YA1819", child.isAdultAndParent),
        ...ynField("TANF", child.receivesTANF, "×"),
        ...ynField("SNAP", child.receivesSNAP, "×"),
        ...ynField("Medical", child.receivesMedicaidOrSSI, "×"),
        ...ynField("Lunch", child.receivesReducedOrFreeLunch, "×"),
        ...ynField("PeachCare", child.receivesPeachcare, "×")
    };

    // Section 3
    const extra = {
        familyUnitSize,
        GrossIncome: household.grossYearlyIncome,
        grossMonthlyIncome,
    };

    // Section 4
    household.members.forEach((member, i) => {
        if (i !== 0) {
            extra[`RelationshipRow${i + 1}`] = member.relationship;
        }

        extra[`Name First Middle and LastRow${i + 1}`] = getFullName(member);
        extra[`Date of Birth ${i !== 0 ? "MMDDYY" : ""}Row${i + 1}`] = new Date(member.dateOfBirth).toLocaleDateString("en-US");
        extra[`Income SourceRow${i + 1}`] = member.monthlyIncome === 0 ? "N/A" : member.incomeSource;
        extra[`Amount Gross Monthly IncomeRow${i + 1}`] = "$" + member.monthlyIncome;
        extra[`How often receivedRow${i + 1}`] = member.monthlyIncome === 0 ? "N/A" : member.oftenReceived;
    });

    data = { ...data, ...extra };

    fillPdfForm(form, data);
    showPdfInNewTab(await pdf.save());
}

export async function printParticipantPdf({ parent, household }, child) {
    const pdf = await readPdf("/ParticipantForm.pdf");
    const form = pdf.getForm();

    const { firstName, middleName, lastName, city, state, gradeLevel, studentID } = child;
    const [YYDob, MMDob, DDDob] = child.dateOfBirth.split('-');

    const data = {
        firstName, middleName, lastName,
        StudentFullName: `${lastName}, ${firstName}`,
        ChildName1: getFullName(child),
        Age1: yearsSinceDate(child.dateOfBirth),
        DOBMMDDYYYY: new Date(child.dateOfBirth).toLocaleDateString("en-US"),
        MMDob, DDDob, YYDob,
        Age: yearsSinceDate(child.dateOfBirth),
        [child.gender === "Other" ? "Female" : `Gender${child.gender}`]: "   X",
        HomeAddress: child.streetAddress,
        "H PO BoxApt": child.aptNumber,
        POBoxAptNum: child.aptNumber,
        city, state,
        ZipCode: child.zipcode,
        ParentPhoneNo: parent.mainPhone,
        gradeLevel,
        SchoolAttending: child.school,
        ...yesNoField("ESOL", child.isESOL, "X"),
        studentID,
        HouseholdCount: household.size,
        [`Check${child.gender}`]: true,
        ...yesNoField("Insurance", child.hasInsuranceProvider),
        InsuranceName: child.insuranceProviderName,
        ...oneOfFields(child.livingSituation, { Other: "Other_2" }),
        ...oneOfFields(child.ethnicity, { Black: "BlackNonHispanic", White: "whiteNonHispanic" }),
        ...yesNoField("SpecialNeeds", child.isSpecialNeeds, "   X"),
        SpecialNeeds: child.specialNeedsDescription,
        ParentName: getFullName(parent),
        ParentAddress: parent.streetAddress + (parent.aptNumber ? `, Apt ${parent.aptNumber}` : ""),
        CityStateZip: `${parent.city}, ${parent.state} ${parent.zipcode}`,
        ...yesNoField("Allergies", child.hasAllergies),
        AllergiesDescription: child.allergiesDescription,
        ...yesNoField("MedicalConditions", child.hasOtherMedicalConditions),
        MedicalDescription: child.otherMedicalConditionsDescription,
        ...yesNoField("Medications", child.isTakingOtherMedications),
        MedicationDescription: child.otherMedicationsDescription,
        EmergencyContactName: parent.emergencyContactName,
        "Relationship to youth": parent.emergencyContactRelationship,
        "Home Phone Number": parent.emergencyContactPhone,
        AlternateContactName: parent.altContactName,
        "Relationship to youth_2": parent.altContactRelationship,
        "Home Phone Number_2": parent.altContactPhone,
        "Legal Name of Parent print": getFullName(parent)
    };

    fillPdfForm(form, data);
    showPdfInNewTab(await pdf.save());
}

function oneOfFields(name, map = {}, value = "X") {
    return { [map[name] ?? name]: value };
}

function ynField(name, condition, value = true) {
    return { [`${name}${condition ? "Y" : "N"}`]: value };
}

function yesNoField(name, condition, value = true) {
    return { [`${name}${condition ? "Yes" : "No"}`]: value };
}

function fillFieldIf(name, condition, value = "X") {
    return condition ? { [name]: value } : {};
}

function fillPdfForm(form, data) {
    const fields = {};
    form.getFields().forEach(f => fields[f.getName().toLowerCase().replace(/[ /]/g, "")] = { name: f.getName(), type: f.constructor.name });

    function setField(key, value) {
        if (Array.isArray(value)) {
            value.forEach((v, i) => setField(key + (i + 1), v));
            return;
        }

        const fieldInfo = fields[key.toLowerCase().replace(/[ /]/g, "")];
        if (!fieldInfo) {
            console.error(`Field ${key} not found in form.`);
            return;
        }
        key = fieldInfo.name;

        if (value === true) {
            try {
                const group = form.getRadioGroup(key);
                group.select(group.getOptions()[0]);
            } catch {
                const checkbox = form.getCheckBox(key);
                checkbox.check();
            }
        } else if (value) {
            form.getField(key).setText(value.toString());
        }
    }

    Object.entries(data).forEach(e => setField(...e));
}

async function readPdf(url) {
    const response = await fetch(url);
    const pdfBytes = await response.arrayBuffer();
    return await PDFDocument.load(pdfBytes);
}

function showPdfInNewTab(pdfBytes) {
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
}